import React, { PropsWithChildren, ReactNode } from "react";
import { LabeledContent } from "buba-components";
import cx from "classnames";

import Separator from "../Separator";
import Text from "../Text";

import styles from "./DescriptionList.scss";

interface DescriptionListItemProps {
  label: string;
  children: ReactNode;
  additionalContentStyles?: string;
  additionalLabelStyles?: string;
  fullWidth?: boolean;
}

interface DescriptionListSectionProps {
  title?: string;
  children: ReactNode;
  customStyles?: string;
}

type DescriptionListProps = {
  customStyles?: string;
  withSeparator?: boolean;
} & PropsWithChildren;

export const DescriptionListItem = ({
  label,
  children,
  additionalContentStyles = "",
  additionalLabelStyles = "",
  fullWidth = false
}: DescriptionListItemProps) => (
  <div
    className={cx(styles.descriptionListItem, "description-list__item", {
      [styles.fullWidth]: fullWidth
    })}
    data-testid="description-list-item"
  >
    <LabeledContent
      label={label}
      additionalContentStyles={additionalContentStyles}
      additionalLabelStyles={additionalLabelStyles}
    >
      {children}
    </LabeledContent>
  </div>
);

export const DescriptionListSection = ({
  title,
  children,
  customStyles
}: DescriptionListSectionProps) => (
  <div
    className={cx(styles.descriptionListSection, customStyles, "description-list__section")}
    data-testid="description-list-section"
  >
    {title && (
      <Text variant="primary" additionalClasses={styles.descriptionListSectionTitle}>
        {title}
      </Text>
    )}
    <div className={styles.descriptionListItems}>{children}</div>
  </div>
);

const DescriptionList = ({
  children,
  customStyles,
  withSeparator = false
}: DescriptionListProps) => {
  const childrenArray = React.Children.toArray(children);

  return (
    <div className={cx(styles.descriptionList, customStyles)} data-testid="description-list">
      {childrenArray.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          {withSeparator && index < childrenArray.length - 1 && (
            <Separator separatorPosition="bottom" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default DescriptionList;
